// Shared type/sort option lists + display metadata for the IT Components page
// (index.vue) and its create/edit slide-over, so labels and colors stay in sync everywhere.
// NOTE: the backend enum key for "Cache" is `caching`, not `cache` — that name
// collides with ActiveRecord::Base.cache. The label here stays "Cache".
// `description` is a one-line blurb shown as the "details" line in the
// searchable Type dropdown (AddItComponentSlideOver) — purely presentational,
// not sent to the backend.
export const TYPE_OPTIONS = [
  { value: 'programming_language', label: 'Programming Language', description: "Languages used to write your application's code" },
  { value: 'runtime', label: 'Runtime', description: "Environments that execute your application's code" },
  { value: 'framework', label: 'Framework', description: 'Libraries that provide structure and reusable functionality' },
  { value: 'database', label: 'Database', description: 'Systems that store and query structured data' },
  { value: 'caching', label: 'Cache', description: 'Layers that speed up access to frequently used data' },
  { value: 'search_engine', label: 'Search Engine', description: 'Engines that index and query large datasets' },
  { value: 'message_broker', label: 'Message Broker', description: 'Services that queue and route messages between systems' },
  { value: 'api_gateway', label: 'API Gateway', description: 'Layers that manage and route API traffic' },
  { value: 'web_server', label: 'Web Server', description: 'Software that serves web requests over HTTP' },
  { value: 'application_server', label: 'Application Server', description: 'Runtime environments that host application logic' },
  { value: 'operating_system', label: 'Operating System', description: 'The base OS your infrastructure runs on' },
  { value: 'container_platform', label: 'Container Platform', description: 'Platforms that package and run containerized workloads' },
  { value: 'virtualization', label: 'Virtualization', description: 'Technology that runs virtual machines on shared hardware' },
  { value: 'cloud_service', label: 'Cloud Service', description: 'Managed services provided by a cloud vendor' },
  { value: 'storage', label: 'Storage', description: 'Systems that store files, objects, or blobs' },
  { value: 'monitoring', label: 'Monitoring', description: 'Tools that track system health and performance' },
  { value: 'logging', label: 'Logging', description: 'Tools that collect and centralize application logs' },
  { value: 'ci_cd', label: 'CI/CD', description: 'Tools that automate build, test, and deployment pipelines' },
  { value: 'identity_access_management', label: 'Identity & Access Management', description: 'Systems that manage authentication and permissions' },
  { value: 'security', label: 'Security', description: 'Tools that protect systems from threats and vulnerabilities' },
  { value: 'networking', label: 'Networking', description: 'Infrastructure that connects and routes traffic' },
  { value: 'analytics', label: 'Analytics', description: 'Tools that measure usage and product metrics' },
  { value: 'developer_tool', label: 'Developer Tool', description: 'Tools that support the software development workflow' },
  { value: 'third_party_service', label: 'Third Party Service', description: 'External services integrated into your stack' },
  { value: 'other', label: 'Other', description: "Anything that doesn't fit the categories above" },
]

// Powers the IT Components page's own sort dropdown (OrgComponentsPanel's
// SortFilterButton) — `value` is what's persisted as the user's sort
// preference (see stores/orgComponents.js), `by`/`order` map straight onto
// the API's own `sort_by`/`sort_order` params. `versions_count` isn't a real
// column on org_components — the backend resolves it via a scalar subquery
// (see OrgComponentsController::VERSIONS_COUNT_SUBQUERY), same pattern as the
// admin catalog's own versions_count/factsheets_count sorts.
export const SORT_OPTIONS = [
  { value: 'created_at-desc', label: 'Recently Added', by: 'created_at', order: 'desc' },
  { value: 'versions_count-desc', label: 'Most Versions', by: 'versions_count', order: 'desc' },
  { value: 'type-asc', label: 'Category', by: 'type', order: 'asc' },
  { value: 'name-asc', label: 'Name (A to Z)', by: 'name', order: 'asc' },
  { value: 'name-desc', label: 'Name (Z to A)', by: 'name', order: 'desc' },
]

// 25 distinct type values is too many for hand picked unique colors, so this
// cycles a fixed palette by index — deterministic and still visually varied.
const BADGE_PALETTE = [
  'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
  'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400',
  'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400',
  'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400',
  'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
]

export function typeLabel(value) {
  return TYPE_OPTIONS.find((o) => o.value === value)?.label || value
}

export function typeBadgeClass(value) {
  const index = TYPE_OPTIONS.findIndex((o) => o.value === value)
  return BADGE_PALETTE[index % BADGE_PALETTE.length] || BADGE_PALETTE.at(-1)
}

// Mirrors the backend's GlobalComponent::CONTEXT_TYPES — which "table" a
// catalog entry belongs to, distinct from its `type` category above.
export const CONTEXT_TYPE_OPTIONS = [
  { value: 'ItComponent', label: 'IT Component' },
  { value: 'Application', label: 'Application' },
]

export function contextTypeLabel(value) {
  return CONTEXT_TYPE_OPTIONS.find((o) => o.value === value)?.label || value
}

const CONTEXT_TYPE_BADGE_CLASSES = {
  ItComponent: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  Application: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
}

export function contextTypeBadgeClass(value) {
  return CONTEXT_TYPE_BADGE_CLASSES[value] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
}
